import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Lightbulb,
  Target,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Copy,
  CheckCircle,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { cn } from '@shared/utils/cn';

interface InterviewPrepProps {
  jobMatchId: string;
}

interface JobMatch {
  id: string;
  job_title: string;
  company_name: string;
}

interface JobAnalysisData {
  talking_points: string[] | null;
  strategy_recommendations: string[] | null;
  strengths: string[] | null;
  weaknesses: string[] | null;
}

interface PrepSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  items: string[];
}

export const InterviewPrep: React.FC<InterviewPrepProps> = ({ jobMatchId }) => {
  const { user } = useAuth();
  const [jobMatch, setJobMatch] = useState<JobMatch | null>(null);
  const [analysis, setAnalysis] = useState<JobAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['talking-points']));
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [jobMatchId]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const { data: matchData, error: matchError } = await supabase
        .from('job_matches')
        .select('id, job_title, company_name')
        .eq('id', jobMatchId)
        .maybeSingle();

      if (matchError) throw matchError;

      if (!matchData) {
        throw new Error('Job match not found');
      }

      setJobMatch(matchData);

      const { data: analysisData, error: analysisError } = await supabase
        .from('job_analysis')
        .select('*')
        .eq('job_match_id', jobMatchId)
        .maybeSingle();

      if (analysisError && analysisError.code !== 'PGRST116') {
        console.warn('Error loading analysis:', analysisError);
      }

      if (analysisData) {
        setAnalysis(analysisData);
      }

      if (user) {
        await supabase
          .from('match_history')
          .insert({
            user_id: user.id,
            job_match_id: jobMatchId,
            interaction_type: 'interview_prep_viewed',
            metadata: { source: 'interview_prep' }
          });
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error('Failed to load interview prep data');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-gray-600">Loading interview preparation...</p>
      </div>
    );
  }

  if (!jobMatch || !analysis) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Analysis Available
        </h3>
        <p className="text-gray-600 mb-4">
          Please generate an analysis first to see interview preparation tips.
        </p>
        <Button onClick={loadData} variant="outline">
          Reload
        </Button>
      </div>
    );
  }

  const sections: PrepSection[] = [
    {
      id: 'talking-points',
      title: 'Key Talking Points',
      icon: <MessageSquare className="h-5 w-5" />,
      color: 'bg-blue-500',
      items: analysis.talking_points || []
    },
    {
      id: 'strategies',
      title: 'Interview Strategies',
      icon: <Target className="h-5 w-5" />,
      color: 'bg-purple-500',
      items: analysis.strategy_recommendations || []
    },
    {
      id: 'strengths',
      title: 'Highlight Your Strengths',
      icon: <Lightbulb className="h-5 w-5" />,
      color: 'bg-green-500',
      items: analysis.strengths || []
    },
    {
      id: 'preparation',
      title: 'Areas to Prepare',
      icon: <BookOpen className="h-5 w-5" />,
      color: 'bg-yellow-500',
      items: analysis.weaknesses || []
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            Interview Preparation
          </h2>
          <p className="text-gray-600">
            {jobMatch.job_title} at {jobMatch.company_name}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Lightbulb className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 mb-1">
                Preparation Tips
              </h4>
              <p className="text-sm text-blue-700">
                Review each section carefully and practice your responses. Click on any item to copy it to your clipboard for easy reference.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const hasItems = section.items.length > 0;

            return (
              <div
                key={section.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn('p-2 rounded-lg text-white', section.color)}>
                      {section.icon}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">{section.title}</h3>
                      <p className="text-sm text-gray-600">
                        {section.items.length} {section.items.length === 1 ? 'item' : 'items'}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                {isExpanded && (
                  <div className="p-4 bg-white">
                    {hasItems ? (
                      <ul className="space-y-3">
                        {section.items.map((item, index) => {
                          const itemId = `${section.id}-${index}`;
                          const isCopied = copiedItem === itemId;

                          return (
                            <li
                              key={index}
                              className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
                            >
                              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-white border border-gray-300 rounded-full text-xs font-medium text-gray-700">
                                {index + 1}
                              </span>
                              <span className="flex-1 text-sm text-gray-700">
                                {item}
                              </span>
                              <button
                                onClick={() => copyToClipboard(item, itemId)}
                                className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Copy to clipboard"
                              >
                                {isCopied ? (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No items in this section
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border/40 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          General Interview Tips
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2">Before the Interview</h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• Research the company culture and values</li>
              <li>• Review your resume and prepare examples</li>
              <li>• Practice common interview questions</li>
              <li>• Prepare questions for the interviewer</li>
            </ul>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-medium text-green-900 mb-2">During the Interview</h4>
            <ul className="space-y-1 text-sm text-green-700">
              <li>• Use the STAR method for responses</li>
              <li>• Show enthusiasm for the role</li>
              <li>• Ask thoughtful questions</li>
              <li>• Listen actively and engage</li>
            </ul>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-medium text-purple-900 mb-2">After the Interview</h4>
            <ul className="space-y-1 text-sm text-purple-700">
              <li>• Send a thank-you email within 24 hours</li>
              <li>• Reflect on questions you struggled with</li>
              <li>• Follow up appropriately on timeline</li>
              <li>• Continue job search until offer accepted</li>
            </ul>
          </div>

          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h4 className="font-medium text-yellow-900 mb-2">Common Mistakes to Avoid</h4>
            <ul className="space-y-1 text-sm text-yellow-700">
              <li>• Arriving late or unprepared</li>
              <li>• Speaking negatively about past employers</li>
              <li>• Not asking any questions</li>
              <li>• Being too casual or informal</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <Button
          onClick={() => window.print()}
          variant="outline"
          className="flex items-center gap-2"
        >
          <BookOpen className="h-4 w-4" />
          Print Preparation Guide
        </Button>
      </div>
    </div>
  );
};

export default InterviewPrep;
